<template>
  <div class="platform-connect-page">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo"></Loader>

    <b-row>
      <b-col md="12" lg="12" sm="12">
        <KTCodePreview>
          <template v-slot:preview>
            <div class="platform-connect-page__header mb-4">
              <h5 class="mb-2">Kết nối sàn thương mại điện tử</h5>
              <p class="text-muted">
                Chọn sàn thương mại điện tử bạn muốn kết nối với hệ thống
              </p>
            </div>

            <div v-if="error" class="alert alert-danger" role="alert">
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">{{ error }}</div>
                <b-button
                  variant="danger"
                  size="sm"
                  @click="retryLastConnection"
                >
                  <i class="flaticon2-refresh"></i> Thử lại
                </b-button>
              </div>
            </div>

            <b-row
              class="platform-connect-page__platforms justify-content-center"
            >
              <b-col md="6" lg="4" class="mb-4">
                <b-card class="platform-card h-100">
                  <div class="platform-card__content text-center">
                    <div class="platform-card__logo mb-3">
                      <img
                        src="/img/platforms/shopee.svg"
                        alt="Shopee"
                        class="platform-logo"
                      />
                    </div>
                    <h5 class="platform-card__title mb-2">Shopee</h5>
                    <p class="platform-card__description text-muted mb-3">
                      Kết nối với Shopee để đồng bộ sản phẩm, đơn hàng và quản
                      lý gian hàng của bạn
                    </p>
                    <ul class="platform-card__features text-left mb-4">
                      <li>Đồng bộ sản phẩm tự động</li>
                      <li>Quản lý đơn hàng tập trung</li>
                      <li>Cập nhật tồn kho realtime</li>
                    </ul>
                    <b-button
                      variant="primary"
                      block
                      :disabled="isConnecting"
                      @click="connectPlatform('shopee')"
                    >
                      <b-spinner
                        v-if="isConnecting && connectingPlatform === 'shopee'"
                        small
                        class="mr-2"
                      ></b-spinner>
                      {{
                        isConnecting && connectingPlatform === 'shopee'
                          ? 'Đang kết nối...'
                          : 'Kết nối Shopee'
                      }}
                    </b-button>
                  </div>
                </b-card>
              </b-col>

              <b-col md="6" lg="4" class="mb-4">
                <b-card class="platform-card platform-card--disabled h-100">
                  <div class="platform-card__content text-center">
                    <div class="platform-card__logo mb-3">
                      <img
                        src="/img/platforms/tiktok.svg"
                        alt="TikTok Shop"
                        class="platform-logo platform-logo--disabled"
                      />
                    </div>
                    <h5 class="platform-card__title mb-2 text-muted">
                      TikTok Shop
                    </h5>
                    <p class="platform-card__description text-muted mb-3">
                      Tính năng kết nối TikTok Shop sẽ sớm được cập nhật
                    </p>
                    <ul
                      class="platform-card__features text-left mb-4 text-muted"
                    >
                      <li>Đồng bộ sản phẩm tự động</li>
                      <li>Quản lý đơn hàng tập trung</li>
                      <li>Cập nhật tồn kho realtime</li>
                    </ul>
                    <b-button variant="secondary" block disabled>
                      Sắp ra mắt
                    </b-button>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <div class="platform-connect-page__info mt-4">
              <b-alert show variant="info">
                <h6 class="alert-heading">
                  <i class="flaticon2-information"></i> Lưu ý khi kết nối
                </h6>
                <ul class="mb-0">
                  <li>
                    Bạn cần có tài khoản người bán trên sàn thương mại điện tử
                  </li>
                  <li>Đảm bảo bạn có quyền quản lý gian hàng</li>
                  <li>
                    Quá trình kết nối sẽ chuyển hướng đến trang đăng nhập của
                    sàn
                  </li>
                  <li>Thông tin kết nối được mã hóa và bảo mật tuyệt đối</li>
                </ul>
              </b-alert>
            </div>
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Loader from '@/view/content/Loader.vue';
import { getToastInstance } from '@/utils/toastHelper';
import { mapGetters } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const platformRepository = RepositoryFactory.get('platformIntegration');

export default {
  name: 'PlatformConnectPage',
  components: {
    KTCodePreview,
    Loader,
  },
  data() {
    return {
      isConnecting: false,
      connectingPlatform: null,
      error: null,
      lastAttemptedPlatform: null,
    };
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
  },
  created() {
    this.loadBreadcrumb();
  },
  methods: {
    ...getToastInstance(),

    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán' },
        { title: 'Kết nối mới' },
      ]);
    },

    async connectPlatform(platform) {
      try {
        this.isConnecting = true;
        this.connectingPlatform = platform;
        this.lastAttemptedPlatform = platform;
        this.error = null;

        const response = await platformRepository.getAuthUrl(platform);

        if (response.authorizationUrl) {
          window.location.href = response.authorizationUrl;
        } else {
          throw new Error('Không nhận được URL xác thực từ server');
        }
      } catch (error) {
        console.error(`Error connecting to ${platform}:`, error);
        this.error = `Không thể kết nối với ${this.getPlatformName(
          platform,
        )}. Vui lòng thử lại.`;
        this.makeToastFailure(
          `Lỗi khi kết nối với ${this.getPlatformName(platform)}`,
        );
        this.isConnecting = false;
        this.connectingPlatform = null;
      }
    },

    retryLastConnection() {
      if (this.lastAttemptedPlatform) {
        this.connectPlatform(this.lastAttemptedPlatform);
      }
    },

    getPlatformName(platform) {
      const names = {
        shopee: 'Shopee',
        tiktok: 'TikTok Shop',
      };
      return names[platform] || platform;
    },
  },
};
</script>

<style lang="scss" scoped>
.platform-connect-page {
  &__header {
    h5 {
      font-weight: 600;
      color: #3f4254;
    }
  }

  &__platforms {
    margin-top: 2rem;
  }

  &__info {
    max-width: 800px;
    margin: 0 auto;
  }
}

.platform-card {
  border: 2px solid #e4e6ef;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

  &:hover:not(&--disabled) {
    border-color: #3699ff;
    box-shadow: 0 4px 12px rgba(54, 153, 255, 0.15);
    transform: translateY(-2px);
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &__content {
    padding: 1rem;
  }

  &__logo {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;

    .platform-logo {
      width: 80px;
      height: 80px;
      object-fit: contain;

      &--disabled {
        filter: grayscale(100%);
        opacity: 0.6;
      }
    }

    .platform-logo-placeholder {
      width: 80px;
      height: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f3f6f9;
      border-radius: 8px;

      .icon-3x {
        font-size: 2.5rem;
      }
    }
  }

  &__title {
    font-weight: 600;
    color: #3f4254;
  }

  &__description {
    font-size: 0.9rem;
    line-height: 1.5;
  }

  &__features {
    list-style: none;
    padding-left: 0;
    font-size: 0.875rem;

    li {
      padding: 0.25rem 0;
      position: relative;
      padding-left: 1.5rem;

      &:before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #1bc5bd;
        font-weight: bold;
      }
    }
  }
}

:deep(.alert-info) {
  background-color: #f1faff;
  border-color: #bee5eb;
  color: #0c5460;

  .alert-heading {
    color: #0c5460;
    font-size: 1rem;
    margin-bottom: 0.75rem;

    i {
      margin-right: 0.5rem;
    }
  }

  ul {
    font-size: 0.875rem;
    padding-left: 1.25rem;

    li {
      margin-bottom: 0.25rem;
    }
  }
}
</style>
